// The local Dashboard/NOW-card power-flow visualization, fed from a cloud
// site's latest 30s report instead of live telemetry. Uses the verbatim
// PowerFlowGraphic port; spoke construction mirrors
// frontend/src/components/LivePowerFlow.tsx (minus relays/badges/history,
// which the cloud does not sync).
import { useTranslation } from "react-i18next";
import { AirVent, Car, Flame, Lightbulb, Plug, Thermometer, Wind, Wrench, Zap } from "./icons";
import type { DeviceState, FlowHistory } from "../api/types";
import PowerFlowGraphic, { FLOW_COLORS, type SlotId, type Spoke } from "./PowerFlowGraphic";
import solarPvImg from "../assets/flow/solar-pv.png";
import gridImg from "../assets/flow/grid.png";
import carImg from "../assets/flow/car.png";
import plugImg from "../assets/flow/plug.png";
import battery0Img from "../assets/flow/battery_0.png";
import battery1Img from "../assets/flow/battery_1.png";
import battery2Img from "../assets/flow/battery_2.png";
import battery3Img from "../assets/flow/battery_3.png";
import battery4Img from "../assets/flow/battery_4.png";
import battery5Img from "../assets/flow/battery_5.png";
import generatorImg from "../assets/flow/generator.png";
import homeImg from "../assets/flow/home.png";
import inverterImg from "../assets/flow/inverter.png";

function fmtPower(w: number): string {
  const abs = Math.abs(w);
  if (abs >= 1000) return `${(w / 1000).toFixed(2)} kW`;
  return `${Math.round(w)} W`;
}

const BATTERY_IMGS = [battery0Img, battery1Img, battery2Img, battery3Img, battery4Img, battery5Img];

function batteryImg(soc: number | undefined): string {
  const idx = soc === undefined ? 5 : Math.max(0, Math.min(5, Math.round(soc / 20.0)));
  return BATTERY_IMGS[idx];
}

function FlowImg({ src }: { src: string }) {
  return <img src={src} alt="" className="h-full w-auto max-w-none object-contain" draggable={false} />;
}

// Same relay icon mapping as the local LivePowerFlow (car/plug get flow
// images, everything else a lucide icon).
function RelayIconEl({ icon, size = 26 }: { icon: string; size?: number }) {
  switch (icon) {
    case "heating":     return <Flame size={size} />;
    case "fan":         return <Wind size={size} />;
    case "ac":          return <AirVent size={size} />;
    case "lightbulb":   return <Lightbulb size={size} />;
    case "wrench":      return <Wrench size={size} />;
    case "zap":         return <Zap size={size} />;
    case "thermometer": return <Thermometer size={size} />;
    case "car":         return <Car size={size} />;
    default:            return <Plug size={size} />;
  }
}

export function CloudPowerFlow({
  state,
  history,
}: {
  state: DeviceState | null;
  /** Last-12h per-spoke series — enables the detailed (sparkline) view. */
  history?: FlowHistory;
}) {
  const { t } = useTranslation();
  const s = state ?? {};
  const pv = s.pv_w ?? 0;
  const bat = s.battery_w ?? 0; // + discharging (register/contract convention)
  const grid = s.grid_w ?? 0; // + import
  const load = s.load_w ?? 0;
  const gen = s.generator_w ?? 0;
  const inverterFault = s.inverter === "error";

  const spokes: Spoke[] = [];

  spokes.push({
    id: "pv",
    title: t("flow.solarPv"),
    value: fmtPower(pv),
    action: t("flow.power"),
    color: FLOW_COLORS.green,
    active: pv > 20,
    direction: "in",
    power: pv,
    icon: <FlowImg src={solarPvImg} />,
    imgW: 125,
  });

  {
    const exporting = grid < 0;
    spokes.push({
      id: "grid",
      title: t("flow.grid"),
      value: fmtPower(Math.abs(grid)),
      action: exporting ? t("flow.export") : t("flow.power"),
      color: exporting ? FLOW_COLORS.green : FLOW_COLORS.red,
      active: Math.abs(grid) > 20,
      direction: exporting ? "out" : "in",
      power: Math.abs(grid),
      icon: <FlowImg src={gridImg} />,
      imgW: 93,
    });
  }

  if (s.soc != null || s.battery_w != null) {
    const charging = bat < 0;
    spokes.push({
      id: "battery",
      title: t("flow.battery"),
      value: fmtPower(Math.abs(bat)),
      action: charging ? t("flow.charging") : t("flow.power"),
      extra: s.soc != null ? (
        <span className="text-[12px] font-semibold">{Math.round(s.soc)}%</span>
      ) : undefined,
      color: charging ? FLOW_COLORS.blue : FLOW_COLORS.green,
      active: Math.abs(bat) > 20,
      direction: charging ? "out" : "in",
      power: Math.abs(bat),
      icon: <FlowImg src={batteryImg(s.soc)} />,
      imgW: 70,
    });
  }

  // Generator card whenever the device reports the metric at all (the local
  // version shows a grey OFF card for a configured-but-idle generator).
  if (s.generator_w != null) {
    const running = gen > 20;
    spokes.push({
      id: "generator",
      title: t("flow.generator"),
      value: fmtPower(gen),
      action: running ? t("flow.power") : t("flow.off"),
      color: running ? FLOW_COLORS.orange : FLOW_COLORS.grey,
      active: running,
      direction: "in",
      power: gen,
      icon: <FlowImg src={generatorImg} />,
      imgW: 145,
    });
  }

  spokes.push({
    id: "house",
    title: t("flow.house"),
    value: fmtPower(load),
    action: t("flow.load"),
    color: FLOW_COLORS.blue,
    active: load > 20,
    direction: "out",
    power: load,
    icon: <FlowImg src={homeImg} />,
    imgW: 250,
  });

  // Relay cards — mirrors the local LivePowerFlow relay spokes.
  for (const relay of (s.relays ?? []).slice(0, 2)) {
    const measured = relay.power_w != null;
    const powerW = relay.power_w ?? 0;
    spokes.push({
      id: relay.id === 1 ? "relay1" : "relay2",
      title: relay.name,
      value: measured ? fmtPower(powerW) : relay.on ? t("flow.on") : t("flow.off"),
      action: t("flow.load"),
      color: FLOW_COLORS.blue,
      active: relay.on && (measured ? powerW > 20 : true),
      direction: "out",
      power: powerW,
      icon:
        relay.icon === "car" ? (
          <FlowImg src={carImg} />
        ) : relay.icon === "plug" ? (
          <FlowImg src={plugImg} />
        ) : (
          <RelayIconEl icon={relay.icon} />
        ),
      imgW: relay.icon === "car" ? 245 : relay.icon === "plug" ? 70 : 40,
    });
  }

  const slotHistory: Partial<Record<SlotId, number[]>> | undefined = history
    ? {
        pv: history.pv,
        grid: history.grid,
        battery: history.battery,
        house: history.load,
        generator: history.generator,
      }
    : undefined;

  return (
    <PowerFlowGraphic
      spokes={spokes}
      history={slotHistory}
      inverter={{
        label: t("flow.inverterLoad"),
        value: fmtPower(load),
        state: inverterFault ? t("flow.fault") : t("flow.acOutput"),
        color: inverterFault ? FLOW_COLORS.red : FLOW_COLORS.blue,
        animate: !inverterFault,
        icon: <FlowImg src={inverterImg} />,
        history: history?.load,
      }}
    />
  );
}
